import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublicAuth } from '@/contexts/PublicAuthContext';
import { toast } from 'sonner';
import { X, Loader2, Phone, User, ChevronRight } from 'lucide-react';

const POLL_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbwhYJtSa3PbpdCgTE-qAN_7RhbQrWjb-YPp-IQmXkS5z4vC7KpxbwRNwDRPN6wVLtI2/exec";

export default function TeaserAuthModal({ isOpen, onClose, pendingVote }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, login } = usePublicAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!isLoginMode && !name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (!password.trim()) {
      toast.error(isLoginMode ? 'Please enter your password' : 'Please create a password');
      return;
    }

    setLoading(true);

    try {
      let userData;
      if (isLoginMode) {
        // Direct login
        userData = await login(phone, password);
        toast.success('Welcome back! Portal unlocked.');
      } else {
        // Try registration, fallback to login if account exists
        try {
          userData = await register(name, phone, password);
          toast.success('Account created! Portal unlocked.');
        } catch (regErr) {
          try {
            userData = await login(phone, password);
            toast.success('Welcome back! Portal unlocked.');
          } catch (loginError) {
            throw regErr;
          }
        }
      }

      // Handle pending vote automatically
      if (pendingVote) {
        if (pendingVote === 'skip') {
          localStorage.setItem("ame_poll_voted_public", "true");
        } else {
          try {
            await fetch(POLL_WEBHOOK_URL, {
              method: "POST",
              body: JSON.stringify({
                action: "vote",
                name: userData?.full_name || name || "Anonymous",
                score_range: pendingVote
              })
            });
            localStorage.setItem("ame_poll_voted_public", "true");
          } catch(err) {
            console.error("Auto-vote failed", err);
          }
        }
      }

      onClose();
      const redirectTarget = localStorage.getItem("auth_redirect_target");
      if (redirectTarget && redirectTarget.startsWith('/') && !redirectTarget.startsWith('//') && !redirectTarget.includes(':')) {
        localStorage.removeItem("auth_redirect_target");
        navigate(redirectTarget);
      } else {
        localStorage.removeItem("auth_redirect_target");
        navigate('/app/home');
      }
    } catch (error) {
      const apiMsg = error?.response?.data?.detail || error.message;
      toast.error(typeof apiMsg === 'string' ? apiMsg : 'Something went wrong. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-[#0f172a]/95 backdrop-blur-xl flex items-center justify-center p-4">
      <div className="bg-[#1e293b] border border-amber-500/20 rounded-[2rem] w-full max-w-md shadow-[0_0_50px_rgba(243,156,18,0.15)] relative p-8 my-8 animate-in zoom-in-95 duration-300">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors p-2 bg-white/5 rounded-full hover:bg-white/10"
        >
          <X size={20} />
        </button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-4 border border-amber-500/20">
            <i className={`fas ${isLoginMode ? 'fa-sign-in-alt' : 'fa-chart-bar'} text-3xl text-amber-500`}></i>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">
            {isLoginMode ? "Welcome Back" : "Unlock Live Results"}
          </h3>
          <p className="text-slate-400 text-sm">
            {isLoginMode 
              ? "Sign in to unlock the full AI College Predictor and detailed counselling charts." 
              : "Join thousands of aspirants to see the live poll results, compare your standing, and unlock the full portal!"
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLoginMode && (
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Your Name</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/40 group-focus-within:text-amber-500 transition-colors">
                  <User size={18} />
                </div>
                <input 
                  required 
                  type="text" 
                  value={name} 
                  onChange={e => setName(e.target.value)} 
                  className="w-full h-14 bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-white/20" 
                  placeholder="Enter your full name" 
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">WhatsApp Number</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/40 group-focus-within:text-amber-500 transition-colors">
                <Phone size={18} />
              </div>
              <input 
                required 
                type="tel" 
                maxLength="10" 
                pattern="[0-9]{10}" 
                value={phone} 
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0,10))} 
                className="w-full h-14 bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-white/20" 
                placeholder="10-digit mobile number" 
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">
              {isLoginMode ? "Password" : "Create Password"}
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-white/40 group-focus-within:text-amber-500 transition-colors">
                <i className="fas fa-lock text-lg"></i>
              </div>
              <input 
                required 
                type="password" 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                className="w-full h-14 bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-white/20" 
                placeholder={isLoginMode ? "Enter your password" : "Enter a secure password"} 
              />
            </div>
          </div>

          <div className="pt-4">
            <button 
              type="submit" 
              disabled={loading} 
              className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-bold text-lg rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)] group"
            >
              {loading ? <Loader2 className="animate-spin" size={24} /> : (
                <>
                  {isLoginMode ? "Log In & Unlock" : "Unlock Portal"} <ChevronRight className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
            
            <p className="text-center text-xs text-slate-400 mt-4">
              {isLoginMode ? (
                <>
                  New student?{" "}
                  <button
                    type="button"
                    onClick={() => setIsLoginMode(false)}
                    className="text-amber-500 hover:underline bg-transparent border-none cursor-pointer font-bold ml-1"
                  >
                    Create Free Profile
                  </button>
                </>
              ) : (
                <>
                  Already registered?{" "}
                  <button
                    type="button"
                    onClick={() => setIsLoginMode(true)}
                    className="text-amber-500 hover:underline bg-transparent border-none cursor-pointer font-bold ml-1"
                  >
                    Log In
                  </button>
                </>
              )}
            </p>
            {!isLoginMode && (
              <p className="text-center text-[10px] text-slate-500 mt-2">By unlocking, you are creating a free account.</p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
